import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import i18n from "@/i18n";

type EventStatusVariant = "default" | "secondary" | "destructive" | "outline";

const eventStatusVariant: Record<string, EventStatusVariant> = {
  draft: "secondary",
  published: "default",
  private: "secondary",
  cancelled: "destructive",
  ended: "outline",
  completed: "outline",
  finished: "outline",
  done: "outline",
};

const eventStatusLabelKey: Record<string, string> = {
  draft: "eventStatusBadge.draft",
  published: "eventStatusBadge.published",
  private: "eventStatusBadge.private",
  cancelled: "eventStatusBadge.cancelled",
  ended: "eventStatusBadge.ended",
  completed: "eventStatusBadge.ended",
  finished: "eventStatusBadge.ended",
  done: "eventStatusBadge.ended",
};

function resolveEffectiveStatusKey(status: string, visibility?: string): string {
  const s = String(status || "").toLowerCase().trim();
  const vis = String(visibility || "").toLowerCase().trim();

  if (s === "draft") return "draft";
  if (s === "cancelled" || s === "canceled") return "cancelled";
  if (s === "ended" || s === "completed" || s === "finished" || s === "done") return s;
  if (vis === "private" || s === "private") return "private";
  return s || status;
}

export function EventStatusBadge({ status, visibility, className }: { status: string; visibility?: string; className?: string }) {
  const { t } = useTranslation("producerCommon");
  const key = resolveEffectiveStatusKey(status, visibility);
  const variant = eventStatusVariant[key] || ("outline" as const);
  const labelKey = eventStatusLabelKey[key];
  const label = labelKey ? t(labelKey) : key;
  return <Badge variant={variant} className={className}>{label}</Badge>;
}

/** Helper for places that just need the label string */
export function getEventStatusLabel(status: string, visibility?: string): string {
  const key = resolveEffectiveStatusKey(status, visibility);
  const labelKey = eventStatusLabelKey[key];
  return labelKey ? i18n.t(labelKey, { ns: "producerCommon" }) : key;
}

/** Helper that returns both label and dot/text styling for sidebar and headers */
export function getEventStatusStyle(status: string, visibility?: string) {
  const key = resolveEffectiveStatusKey(status, visibility);
  const labelKey = eventStatusLabelKey[key];
  const label = labelKey ? i18n.t(labelKey, { ns: "producerCommon" }) : key;

  switch (key) {
    case "draft":
      return {
        label,
        textColor: "text-amber-600 dark:text-amber-400",
        dotColor: "bg-amber-500",
      };
    case "private":
      return {
        label,
        textColor: "text-muted-foreground",
        dotColor: "bg-muted-foreground",
      };
    case "cancelled":
      return {
        label,
        textColor: "text-red-600 dark:text-red-400",
        dotColor: "bg-red-500",
      };
    case "ended":
    case "completed":
    case "finished":
    case "done":
      return {
        label,
        textColor: "text-muted-foreground",
        dotColor: "bg-muted-foreground",
      };
    case "published":
    default:
      return {
        label,
        textColor: "text-emerald-600 dark:text-emerald-400",
        dotColor: "bg-emerald-500",
      };
  }
}

